'use client';

import { NotInStockDialog } from '@eliadmin/core/src/components/product/dialog/not-in-stock-dialog';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useDialogStore } from '@eliadmin/core/src/components/ui/dialog';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import {
  getProductMinQuantity,
  getProductQuantityRules,
  getProductQuantityStep,
  isSellable,
  normalizeProductQuantity,
  normalizeProductQuantityDown,
} from '@eliadmin/core/src/utils/product-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useMemo } from 'react';
import { LuMinus, LuPlus } from 'react-icons/lu';

export function Quantity({ className }: { className?: string }) {
  const { t } = useTranslations();
  const product = useVariantsStore(state => state.product);
  const selectedQuantity = useVariantsStore(state => state.selectedQuantity);
  const setSelectedQuantity = useVariantsStore(state => state.setSelectedQuantity);
  const openDialog = useDialogStore(s => s.open);

  const quantityRules = useMemo(() => getProductQuantityRules(product), [product]);
  const minQuantity = useMemo(() => getProductMinQuantity(quantityRules), [quantityRules]);
  const step = useMemo(() => getProductQuantityStep(quantityRules), [quantityRules]);

  const disabled = product.quantity < 1 && !isSellable(product, minQuantity);

  const handleChangeQuantity = (delta: number) => {
    const newValue = normalizeProductQuantity(selectedQuantity + delta, quantityRules);
    const sellableNext = isSellable(product, newValue);

    if (!sellableNext && product.quantity > 0) {
      openDialog({
        id: NotInStockDialog.id,
        content: <NotInStockDialog maxQuantity={product.quantity} unit={product.unit ?? ''} />,
      });
      setSelectedQuantity(normalizeProductQuantityDown(product.quantity, quantityRules));
    } else if (sellableNext) {
      setSelectedQuantity(newValue);
    }
  };

  if (disabled) return null;

  return (
    <div
      className={cn(
        'flex w-full items-center justify-center gap-1 rounded-[16px] border px-3 sm:w-fit',
        'ea-product-card__quantity',
        className,
      )}
      onClick={e => {
        e.stopPropagation();
        e.preventDefault();
      }}
    >
      <Button
        type="button"
        aria-label={`${t('text22')} −`}
        variant="none"
        size="none"
        className="ea-product-card__quantity-decrease-button"
        disabled={selectedQuantity <= minQuantity}
        onClick={() => handleChangeQuantity(-step)}
      >
        <LuMinus size={16} strokeWidth={3} className="text-primary" />
      </Button>
      <div className="h-[50px] w-full min-w-7 max-w-7 flex-grow sm:w-fit sm:flex-grow-0">
        <Input
          aria-label={t('text22')}
          className={{
            wrapper:
              'h-[50px] w-full min-w-7 max-w-7 flex-grow border-none sm:w-fit sm:flex-grow-0',
            main: 'border-none p-0 text-center font-semibold',
          }}
          value={selectedQuantity}
          onChange={e => {
            const value = parseInt(e.target.value, 10);
            if (!isNaN(value) && value > 0) {
              setSelectedQuantity(normalizeProductQuantity(value, quantityRules));
            }
          }}
          onFocus={e => e.target.select()}
        />
      </div>
      <Button
        type="button"
        aria-label={`${t('text22')} +`}
        variant="none"
        size="none"
        className="ea-product-card__quantity-increase-button"
        onClick={() => handleChangeQuantity(step)}
      >
        <LuPlus size={16} strokeWidth={3} className="text-primary" />
      </Button>
    </div>
  );
}
